import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFeatureFlags, getResolvedFeatureFlags } from '../api/config';
import logger from '../utils/logger';

/**
 * Feature flags for controlling application features.
 * Default to false - features must be explicitly enabled.
 * 
 * When authenticated, flags are resolved with user-specific overrides:
 * User Override → Tier Override → Global Default
 */

// Default flags (all false except loaded)
const DEFAULT_FLAGS = {
  chat: false, // AI Chat feature
  stocks: false,
  llmRules: false,
  feedback: false, // New feedback flag
  journalPage: false, // Journal page access (separate from journal save/history)
  pipelineWebhook: false, // Pipeline webhook feature for external integrations
  analytics: false, // Analytics Pipeline feature for market direction analysis
  analyticsLlmRules: false, // AI-powered analytics modules (e.g., Multi-Timeframe AI)
  analyticsWebhook: false, // Analytics webhook for external integrations
  tradeHistoryWebhook: false, // Trade history webhook for external integrations
  userApiKeys: true, // Users must provide their own API keys for LLM usage
  appOpenAiKey: false, // App-level OpenAI key (disabled — users bring their own)
  appAnthropicKey: false, // App-level Anthropic key available as fallback
  loaded: false,
};

const FeatureFlagContext = createContext(DEFAULT_FLAGS);

export const FeatureFlagProvider = ({ children, authToken }) => {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);

  const loadFlags = useCallback(async (token) => {
    try {
      // If we have an auth token, get resolved flags with user overrides
      // Otherwise, fall back to public global flags
      const response = token
        ? await getResolvedFeatureFlags(token)
        : await getFeatureFlags();
      logger.log('Feature flags loaded:', response, token ? '(resolved for user)' : '(public)');
      setFlags({ ...DEFAULT_FLAGS, ...response, loaded: true });
    } catch (error) {
      logger.error('Failed to load feature flags:', error);
      // Keep default flags but mark as loaded
      setFlags((prev) => ({ ...prev, loaded: true }));
    }
  }, []);

  useEffect(() => {
    loadFlags(authToken);
  }, [loadFlags, authToken]);

  // Refresh flags function (useful after admin updates)
  const refreshFlags = useCallback(async () => {
    try {
      const response = authToken
        ? await getResolvedFeatureFlags(authToken)
        : await getFeatureFlags();
      setFlags({ ...DEFAULT_FLAGS, ...response, loaded: true });
    } catch (error) {
      logger.error('Failed to refresh feature flags:', error);
    }
  }, [authToken]);

  const value = {
    ...flags,
    refreshFlags,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access all feature flags
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

// Convenience hooks for individual features
export const useChatEnabled = () => useFeatureFlags().chat;
export const useJournalEnabled = () => useFeatureFlags().journal;
export const useJournalPageEnabled = () => useFeatureFlags().journalPage;
export const useCryptoEnabled = () => useFeatureFlags().crypto;
export const useStocksEnabled = () => useFeatureFlags().stocks;
export const useLlmRulesEnabled = () => useFeatureFlags().llmRules;
export const useFeedbackEnabled = () => useFeatureFlags().feedback;
export const usePipelineWebhookEnabled = () => useFeatureFlags().pipelineWebhook;
export const useAnalyticsEnabled = () => useFeatureFlags().analytics;
export const useAnalyticsLlmRulesEnabled = () => useFeatureFlags().analyticsLlmRules;
export const useAnalyticsWebhookEnabled = () => useFeatureFlags().analyticsWebhook;
export const useTradeHistoryWebhookEnabled = () => useFeatureFlags().tradeHistoryWebhook;
export const useUserApiKeysEnabled = () => useFeatureFlags().userApiKeys;
export const useFlagsLoaded = () => useFeatureFlags().loaded;

